import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';

const PrivacyPolicy = () => {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      updated="October 13, 2026"
    >
      <Typography paragraph>
        This Privacy Policy explains how GoCourier Inc., the operator
        of HaggleFreight ("HaggleFreight," "we," "us," or "our"),
        collects, uses, discloses, stores, and deletes personal
        information through the HaggleFreight website, mobile
        applications, and related marketplace services
        (the "Platform").
      </Typography>

      <Typography paragraph>
        HaggleFreight is a transportation marketplace connecting
        customers and shippers with independent drivers, carriers,
        and transportation companies. This policy covers information
        processed in connection with those marketplace activities.
        References to optional features apply when those features
        are available and used; they do not authorize undisclosed
        collection for a future feature.
      </Typography>

      <Typography paragraph>
        For privacy assistance, contact support@hagglefreight.com.
        To request account deletion, follow Section 11 below.
        Acknowledging this policy does not constitute blanket
        consent to optional marketing, tracking, or other processing
        that requires a separate permission.
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Information We Collect
      </Typography>

      <Typography paragraph>
        Account information includes the information you submit when
        registering or managing an account, such as your name, email
        address, telephone number, account role, business details,
        authentication information, preferences, and relevant
        agreement-acceptance records.
      </Typography>

      <Typography paragraph>
        Shipment and transaction information includes pickup and
        delivery addresses, contact details, cargo descriptions,
        dimensions, weight, handling requirements, timing, bids,
        negotiated terms, transaction status, order history, and
        related documents.
      </Typography>

      <Typography paragraph>
        Transportation Provider information includes the driver,
        business, vehicle, service-area, licensing, insurance, and
        operating-authority information submitted through available
        account or verification features. Uploaded documents may
        contain additional personal information.
      </Typography>

      <Typography paragraph>
        Communications and content include marketplace messages,
        support requests, reports, photographs, documents, reviews,
        delivery records, and other information submitted through
        the Platform. We also receive information about you from
        transaction participants, such as when someone identifies
        you as a pickup contact or submits a report involving your
        account.
      </Typography>

      <Typography paragraph>
        Our systems and service providers process technical
        information associated with access to the Platform, such
        as IP addresses, browser and device information, operating
        system, app version, request timestamps, access records,
        and error or diagnostic information. Additional device
        information depends on the permissions and features used.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. How We Use Information
      </Typography>

      <Typography paragraph>
        We use information to authenticate accounts, display
        shipment opportunities, transmit bids and messages, record
        accepted transaction terms, provide enabled payment
        features, communicate service updates, and respond to
        support requests.
      </Typography>

      <Typography paragraph>
        Relevant information is also used to maintain service
        reliability, investigate reported abuse and security
        incidents, prevent fraud, enforce valid agreements, maintain
        necessary records, and meet legal obligations.
      </Typography>

      <Typography paragraph>
        Optional analytics, promotional communications, and other
        consent-based processing remain subject to the applicable
        disclosures and choices. We limit processing to information
        reasonably relevant to the disclosed purpose. A materially
        different use requires updated disclosures and any
        additional permission required before that use begins.
      </Typography>

      <Typography variant="h5" gutterBottom>
        3. Payments
      </Typography>

      <Typography paragraph>
        When payment or payout features are enabled, an identified
        payment provider, such as Stripe, processes the financial
        information entered through its payment or onboarding
        interface. Payment providers may collect billing details,
        card or bank information, identity-verification information,
        transaction records, and fraud-prevention information under
        their own applicable notices.
      </Typography>

      <Typography paragraph>
        HaggleFreight uses payment-related information such as
        transaction identifiers, amounts, payment and refund status,
        billing information, and limited payment-method details to
        administer authorized transactions and provide support.
        Our intended payment flow does not require storing complete
        payment-card numbers or card security codes on our own
        servers.
      </Typography>

      <Typography paragraph>
        Enter financial credentials only in the intended payment
        interface. Do not send passwords, card security codes,
        complete card numbers, or bank-login credentials through
        ordinary messages or support email. This policy does not
        authorize a charge, subscription, or payout deduction;
        payment authorization occurs through the applicable
        transaction or purchase process.
      </Typography>

      <Typography variant="h5" gutterBottom>
        4. Location and Tracking Information
      </Typography>

      <Typography paragraph>
        Pickup and delivery addresses and locations entered by
        users are shipment information. They are different from
        precise location obtained directly from a device.
      </Typography>

      <Typography paragraph>
        When you use an available location-based feature and grant
        the applicable permissions, the Platform may process
        coordinates, timestamps, relevant route information, and
        location updates for that feature. Uses may include
        displaying relevant opportunities, identifying shipment
        locations, or supporting an enabled shipment-tracking
        function.
      </Typography>

      <Typography paragraph>
        Background collection, meaning location access while the
        app is not open or actively in use, requires an appropriate
        feature-specific explanation and applicable permissions
        before collection begins. General acceptance of this
        policy is not authorization for undisclosed background
        tracking.
      </Typography>

      <Typography paragraph>
        You can manage device-location permissions through device
        settings and available in-app controls. Restricting a
        permission may prevent the associated feature from working.
        Revoking device access does not automatically delete
        addresses you entered or earlier transaction records.
      </Typography>

      <Typography variant="h5" gutterBottom>
        5. Uploaded Content and Delivery Records
      </Typography>

      <Typography paragraph>
        When you submit photographs, signatures, documents, notes,
        or other records, we process that content and relevant
        metadata for the associated account, shipment, support,
        safety, or dispute purpose described to you.
      </Typography>

      <Typography paragraph>
        Camera, photo, and file access depend on the feature and
        applicable device permissions. Upload only information you
        have authority to provide and that is reasonably necessary.
        Submission of a private document does not authorize its
        unrelated public display or advertising use.
      </Typography>

      <Typography paragraph>
        Delivery records may be visible to relevant transaction
        participants. A record can contain information about
        recipients, staff, drivers, or locations; avoid including
        unrelated people or unnecessary sensitive details.
      </Typography>

      <Typography variant="h5" gutterBottom>
        6. Sharing Information
      </Typography>

      <Typography paragraph>
        Listing information is displayed to the users who can access
        the relevant marketplace listing or opportunity. Transaction
        participants receive information needed for their
        interaction, which may include shipment details, relevant
        contact information, provider information, messages, status
        updates, and submitted delivery records.
      </Typography>

      <Typography paragraph>
        The audience depends on the feature and transaction.
        Review available visibility information before posting,
        and avoid placing private information in broadly visible
        listing fields. Personnel authorized to administer a
        business account may have access to that account's
        activities and records.
      </Typography>

      <Typography paragraph>
        We disclose information to service providers for authorized
        operational functions, for appropriate legal and safety
        purposes described below, and when you specifically request
        or authorize a disclosure.
      </Typography>

      <Typography paragraph>
        We do not sell personal information or disclose it for
        cross-context behavioral or targeted advertising. This
        commitment does not mean that ordinary transaction
        information is never shared with a carrier, customer,
        payment provider, or other authorized recipient.
      </Typography>

      <Typography paragraph>
        A legitimate merger, reorganization, financing, or transfer
        of the relevant business may involve information subject
        to confidentiality protections, applicable law, and the
        commitments governing that information. A business transfer
        does not authorize unrelated new uses without required
        notice and permission.
      </Typography>

      <Typography variant="h5" gutterBottom>
        7. Service Providers, Storage, and Automated Tools
      </Typography>

      <Typography paragraph>
        Service providers support functions such as hosting,
        database and file storage, payments, communications,
        maps, diagnostics, permitted analytics, and customer
        support. Providers acting on our behalf are authorized to
        process information for their assigned functions, not for
        unrestricted unrelated uses.
      </Typography>

      <Typography paragraph>
        We require appropriate protections from providers acting
        on our behalf, including limits on access and use and
        assistance with applicable security and deletion
        obligations. Independent payment providers and transaction
        participants may also process information under their own
        legal responsibilities and privacy notices.
      </Typography>

      <Typography paragraph>
        Browser cookies, local storage, and app storage may support
        authentication, session security, and preferences. Blocking
        or clearing necessary storage can sign you out or affect
        features. Optional analytics remain subject to applicable
        notices and choices.
      </Typography>

      <Typography paragraph>
        Removing a name does not necessarily make information
        anonymous. Information that remains reasonably linkable to
        an account, device, or person is treated as personal
        information. Aggregate or deidentified information must be
        handled with appropriate safeguards against reidentification.
      </Typography>

      <Typography paragraph>
        Before personal information is shared with an external AI
        provider, the relevant feature must explain the recipient
        and purpose and obtain any required explicit permission.
        This policy does not authorize unrestricted external AI
        access or unrelated training on private messages and
        documents.
      </Typography>

      <Typography paragraph>
        GoCourier Inc. operates from the United States. Information
        may be processed where we and our providers operate.
        International transfers remain subject to applicable legal
        safeguards; acknowledging this policy does not waive
        those requirements.
      </Typography>

      <Typography variant="h5" gutterBottom>
        8. Legal and Safety Disclosures
      </Typography>

      <Typography paragraph>
        We may disclose information when reasonably necessary and
        legally appropriate to comply with valid legal process,
        meet a legal obligation, investigate fraud or abuse,
        address an immediate safety risk, or establish or defend
        legal rights.
      </Typography>

      <Typography paragraph>
        A request from another person, business, insurer, or
        authority is not automatically permission to disclose all
        account information. We evaluate the request and the
        applicable legal basis and limit disclosure as appropriate.
      </Typography>

      <Typography variant="h5" gutterBottom>
        9. Data Security
      </Typography>

      <Typography paragraph>
        We use administrative, technical, and organizational
        measures appropriate to the information and risks involved.
        These measures are intended to support secure transmission,
        restrict unauthorized access, and protect information
        handled by the Platform and providers acting on our behalf.
      </Typography>

      <Typography paragraph>
        No system guarantees absolute security. This limitation
        does not eliminate our responsibilities to maintain
        appropriate safeguards, investigate incidents, or provide
        legally required notices.
      </Typography>

      <Typography paragraph>
        Protect your credentials and promptly report suspected
        unauthorized access. Do not disclose unnecessary sensitive
        information through shipment fields, chat, or email.
      </Typography>

      <Typography variant="h5" gutterBottom>
        10. Data Retention
      </Typography>

      <Typography paragraph>
        During an active account, retention depends on the purpose
        of the information, the service being provided, its
        sensitivity, applicable legal requirements, and relevant
        operational limits. We do not treat the possibility that
        information might someday be useful as a justification
        for indefinite retention.
      </Typography>

      <Typography paragraph>
        Account deletion is governed by Section 11. Following
        deletion, specific identifiable records are retained only
        when legally required, such as records subject to an
        applicable transaction, tax, accounting, regulatory, or
        evidence-preservation obligation. We restrict their use
        and remove them when the required retention period ends.
      </Typography>

      <Typography paragraph>
        Backup copies are subject to a controlled deletion or
        replacement process rather than ordinary continued use.
        Any remaining technical deletion steps must be explained
        when relevant to a request. If a backup is restored,
        applicable deletion instructions must be reapplied.
      </Typography>

      <Typography variant="h5" gutterBottom id="account-deletion">
        11. Account Deletion
      </Typography>

      <Typography paragraph>
        Customers and Transportation Providers can initiate deletion
        from account settings by selecting "Request Account
        Deletion" and following the confirmation steps.
      </Typography>

      <Typography paragraph>
        You may also request deletion without installing or
        reinstalling the app. Email support@hagglefreight.com with
        the subject "HaggleFreight Account Deletion" and identify
        the account email and account type. Do not include your
        password, complete payment-card number, or bank credentials.
      </Typography>

      <Typography paragraph>
        We may use proportionate identity verification to prevent
        unauthorized deletion. Requests may be processed manually.
        We will explain the expected processing time and confirm
        completion within applicable legal requirements.
      </Typography>

      <Typography paragraph>
        Deletion removes the account and associated personal
        information and User Content, rather than merely freezing,
        suspending, or deactivating access. We address relevant
        information held by service providers acting on our behalf
        as part of the process.
      </Typography>

      <Typography paragraph>
        Specific records are retained only when legally required,
        as explained in Section 10. We will identify applicable
        categories and reasons and limit retention to the necessary
        information and period. An outstanding transaction or
        payment does not prevent you from initiating deletion.
      </Typography>

      <Typography paragraph>
        Other transaction participants may separately retain
        information they lawfully received and are responsible for
        their own handling of it. Their copies do not authorize
        HaggleFreight to keep otherwise-deletable information.
      </Typography>

      <Typography paragraph>
        Deleting an account may not cancel a subscription purchased
        through an app store. We will provide relevant cancellation
        information. For a subscription billed directly by
        GoCourier Inc., future renewals will be stopped as part of
        processing deletion. You do not need to wait for a
        subscription period to end to request account deletion.
      </Typography>

      <Typography variant="h5" gutterBottom>
        12. User Choices and Privacy Requests
      </Typography>

      <Typography paragraph>
        You can manage available account details and preferences
        through the Platform and relevant device permissions
        through device settings. Optional promotional messages
        can be managed using available preferences, unsubscribe
        instructions, or a request to support.
      </Typography>

      <Typography paragraph>
        We honor applicable optional-analytics choices. Disabling
        optional analytics does not necessarily stop essential
        security and operational records needed to provide the
        service. We do not describe linkable information as
        anonymous merely because direct names were removed.
      </Typography>

      <Typography paragraph>
        Depending on applicable law, you may have rights to access,
        correct, delete, or obtain a copy of information; withdraw
        consent; object to or restrict processing; and opt out of
        specified uses or qualifying automated decisions.
      </Typography>

      <Typography paragraph>
        Contact support@hagglefreight.com with your request. We may
        verify identity or an authorized agent's authority using
        information proportionate to the request. We will respond
        within applicable deadlines and explain any lawful
        limitation or refusal.
      </Typography>

      <Typography paragraph>
        Where an appeal is available, email the same address with
        the subject "HaggleFreight Privacy Appeal" and identify the
        decision to be reviewed. You may also contact an appropriate
        regulator. We will not unlawfully discriminate against you
        for exercising privacy rights.
      </Typography>

      <Typography paragraph>
        We honor legally applicable opt-out preference signals for
        the processing to which they apply. Withdrawing permission
        may affect a feature that genuinely requires the
        information, but it does not authorize unrelated penalties.
      </Typography>

      <Typography variant="h5" gutterBottom>
        13. Children's Privacy
      </Typography>

      <Typography paragraph>
        HaggleFreight is intended for users who are at least
        18 years old and is not directed to children. Do not create
        an account for a child or submit unnecessary information
        about children.
      </Typography>

      <Typography paragraph>
        Contact support if you believe a child has created an
        account or information has been collected inappropriately
        so we can investigate and take appropriate action.
      </Typography>

      <Typography variant="h5" gutterBottom>
        14. Changes to This Privacy Policy
      </Typography>

      <Typography paragraph>
        We will update the displayed date when this policy changes.
        Material changes will be communicated through appropriate
        Platform or account notices before new practices begin,
        except where applicable law permits or requires a different
        process.
      </Typography>

      <Typography paragraph>
        We will obtain additional permission when required.
        Updating the footer date or continued use alone does not
        authorize a materially different use that requires
        separate consent.
      </Typography>

      <Typography variant="h5" gutterBottom>
        15. Contact
      </Typography>

      <Typography paragraph>
        Privacy questions, account-deletion requests, and other
        privacy requests should be directed to GoCourier Inc.,
        operator of HaggleFreight, at support@hagglefreight.com.
        Provide only the information reasonably needed to identify
        your request.
      </Typography>
    </LegalPageLayout>
  );
};

export default PrivacyPolicy;